import React, { useCallback } from 'react';
import { useEnquiryStore } from '../../store/useEnquiryStore';
import { Attachment } from '../../types';

export const AttachmentsForm: React.FC = () => {
  const { currentEnquiry, addAttachment, removeAttachment } = useEnquiryStore();

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return;
      }

      const attachment: Attachment = {
        id: crypto.randomUUID(),
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
        file: file
      };

      addAttachment(attachment);
    });

    // Reset the input
    event.target.value = '';
  }, [addAttachment]);

  const handleRemoveAttachment = (id: string) => {
    const attachment = currentEnquiry.attachments.find(a => a.id === id);
    if (attachment) {
      URL.revokeObjectURL(attachment.url);
      removeAttachment(id);
    }
  };

  const isImage = (type: string) => type.startsWith('image/');

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Photos & Sketches</h2>
        <p className="text-sm text-gray-600">
          Upload photos of the installation area, sketches, or any other relevant documents.
        </p>
      </div>

      <div className="space-y-6">
        {/* Upload Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
          <div className="space-y-2">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="text-sm text-gray-600">
              <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                <span>Upload files</span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                />
              </label>
              <span className="pl-1">or drag and drop</span>
            </div>
            <p className="text-xs text-gray-500">
              PNG, JPG, PDF, DOC up to 10MB each
            </p>
          </div>
        </div>

        {/* Attachments List */}
        {currentEnquiry.attachments.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Attached Files</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentEnquiry.attachments.map((attachment) => (
                <div key={attachment.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {isImage(attachment.type) ? (
                        <div className="mb-2">
                          <img
                            src={attachment.url}
                            alt={attachment.name}
                            className="w-full h-32 object-cover rounded-md"
                          />
                        </div>
                      ) : (
                        <div className="mb-2 flex items-center justify-center w-full h-32 bg-gray-100 rounded-md">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="text-sm">
                        <p className="font-medium text-gray-900 truncate" title={attachment.name}>
                          {attachment.name}
                        </p>
                        <p className="text-gray-500">
                          {formatFileSize(attachment.file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveAttachment(attachment.id)}
                      className="ml-2 text-red-600 hover:text-red-800"
                      title="Remove attachment"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Panel */}
        <div className="bg-blue-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Upload Guidelines</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Take clear photos of windows, doors, and installation areas</li>
                  <li>Include measurements or reference objects for scale</li>
                  <li>Capture any obstacles or special considerations</li>
                  <li>Sketch drawings can help clarify complex installations</li>
                  <li>All files are stored temporarily for this enquiry session</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};